import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom'
import AdminPage from './pages/AdminPage'
import Alerts from './pages/Alerts'
import AuthForm from './pages/AuthForm'
import Dashboard from './pages/Dashboard'
import Logs from './pages/Logs'
import Profile from './pages/Profile'
import RegionsStatsPage from './pages/RegionsStatsPage'
import SchoolDashboard from './pages/SchoolDashboard'
import HikvisionDeviceDashboard from './pages/HikvisionDeviceDashboard'
import HikvisionNvrDashboard from './pages/HikvisionNvrDashboard'
import PanicStationDashboard from './pages/PanicStationDashboard'
import SpeedtestResults from './pages/SpeedtestResults'
import PanicIncidents from './pages/PanicIncidents'
import FireIncidents from './pages/FireIncidents'
import Equipment from './pages/Equipment'
import Assistant from './pages/Assistant'
import FireOverlay from './components/FireOverlay'
import { AssistantSessionProvider } from './context/AssistantSessionContext'
import { PanicResponseProvider } from './context/PanicResponseContext'
import { useFireSocket } from './hooks/useFireSocket'
import { acceptFireIncident } from './api/fire'
import axiosInstance from './utils/axiosInstance'
import {
  clearCanRespondToIncidents,
  persistCanRespondToIncidents,
  readCanRespondToIncidents,
} from './lib/incidentAccess'

const LoadingScreen = () => {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-base">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent"></div>
        <p className="text-sm text-muted-400">{t('common.loading')}</p>
      </div>
    </div>
  );
};

function isTokenValid(token) {
  try {
    const decoded = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime;
  } catch (err) {
    return false;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [ruijieConnected, setRuijieConnected] = useState(false);
  const [userId, setUserId] = useState(null);
  const [fireOverlayIncident, setFireOverlayIncident] = useState(null);
  const [fireDismissedIds, setFireDismissedIds] = useState(() => new Set());
  const [fireAcceptLoading, setFireAcceptLoading] = useState(false);
  const [fireNotice, setFireNotice] = useState(null);
  const [canRespondToIncidents, setCanRespondToIncidents] = useState(() => readCanRespondToIncidents());
  const { t } = useTranslation();

  const applyIncidentAccess = React.useCallback((value) => {
    const allowed = Boolean(value);
    persistCanRespondToIncidents(allowed);
    setCanRespondToIncidents(allowed);
  }, []);

  const handleFireIncident = React.useCallback((payload) => {
    if (!canRespondToIncidents) return;

    if (payload?.status === 'closed') {
      setFireOverlayIncident((prev) => (prev?.id === payload.id ? null : prev));
      window.dispatchEvent(new CustomEvent('fireIncidentUpdated', { detail: payload }));
      return;
    }

    if (payload?.status === 'accepted') {
      setFireOverlayIncident((prev) => (prev?.id === payload.id ? null : prev));
      window.dispatchEvent(new CustomEvent('fireIncidentUpdated', { detail: payload }));
      return;
    }

    if (payload?.status === 'open' && !fireDismissedIds.has(payload.id)) {
      setFireOverlayIncident(payload);
      window.dispatchEvent(new CustomEvent('fireIncidentUpdated', { detail: payload }));
    }
  }, [fireDismissedIds, canRespondToIncidents]);

  useFireSocket(isAuthenticated ? handleFireIncident : null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('userRole');
    const email = localStorage.getItem('userEmail');

    if (token && isTokenValid(token)) {
      try {
        // Получаем роль из токена для большей надежности
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        const tokenRole = tokenData.role ? (typeof tokenData.role === 'string' ? tokenData.role.trim().toLowerCase() : tokenData.role) : role;
        
        // Синхронизируем роль в localStorage с ролью из токена
        if (tokenRole && tokenRole !== role) {
          localStorage.setItem('userRole', tokenRole);
        }
        
        setIsAuthenticated(true);
        setUserRole(tokenRole || role);
        setUserEmail(tokenData.email || email);
        setUserId(tokenData.id ?? null);
        setCanRespondToIncidents(readCanRespondToIncidents());
        axiosInstance.get('/profile').then((res) => {
          if (typeof res.data?.canRespondToIncidents === 'boolean') {
            applyIncidentAccess(res.data.canRespondToIncidents);
          }
        }).catch(() => {});

        // Проверяем наличие сохраненной Ruijie сессии
        const ruijieSession = localStorage.getItem('ruijieSession');
        if (ruijieSession) {
          try {
            const session = JSON.parse(ruijieSession);
            // Проверяем не истекла ли сессия (8 часов)
            if (session.timestamp && Date.now() - session.timestamp < 8 * 60 * 60 * 1000) {
              setRuijieConnected(true);
            } else {
              localStorage.removeItem('ruijieSession');
            }
          } catch (e) {
            localStorage.removeItem('ruijieSession');
          }
        }
      } catch (e) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('ruijieSession');
        clearCanRespondToIncidents();
        setCanRespondToIncidents(false);
        setIsAuthenticated(false);
      }
    } else {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('ruijieSession');
      clearCanRespondToIncidents();
      setCanRespondToIncidents(false);
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, [applyIncidentAccess]);

  // Слушаем событие готовности Ruijie сессии
  useEffect(() => {
    const handleRuijieReady = (event) => {
      console.log('[App] Ruijie session ready:', event.detail);
      setRuijieConnected(true);
    };

    window.addEventListener('ruijieSessionReady', handleRuijieReady);
    return () => {
      window.removeEventListener('ruijieSessionReady', handleRuijieReady);
    };
  }, []);

  const handleLoginSuccess = (token, role, incidentAccess) => {
    // Нормализуем роль (убираем пробелы и приводим к нижнему регистру)
    const normalizedRole = typeof role === 'string' ? role.trim().toLowerCase() : role;
    
    localStorage.setItem('authToken', token);
    localStorage.setItem('userRole', normalizedRole);
    applyIncidentAccess(incidentAccess);

    const userInfo = JSON.parse(atob(token.split('.')[1]));
    localStorage.setItem('userEmail', userInfo.email);

    // Используем роль из токена, если она отличается от переданной
    const tokenRole = userInfo.role ? (typeof userInfo.role === 'string' ? userInfo.role.trim().toLowerCase() : userInfo.role) : normalizedRole;
    setUserRole(tokenRole);
    setUserEmail(userInfo.email);
    setUserId(userInfo.id ?? null);
    setIsAuthenticated(true);

    // Отправляем кастомное событие для обновления данных в других компонентах
    window.dispatchEvent(new CustomEvent('authTokenUpdated', { detail: { token, role: tokenRole } }));

    // Ruijie SSO теперь выполняется автоматически в AuthForm
    // Результат будет получен через событие 'ruijieSessionReady'
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('authToken');
    
    // Выходим из Ruijie сессии на бэкенде (удаляет SESSION cookie)
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL || ''}/api/ruijie/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        console.log('[Logout] Ruijie session cleared');
      } catch (err) {
        console.warn('[Logout] Failed to clear Ruijie session:', err);
      }
    }

    // Очищаем локальные данные
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('ruijieSession');
    localStorage.removeItem('ruijieSessionStorageData');
    clearCanRespondToIncidents();
    
    setIsAuthenticated(false);
    setUserRole(null);
    setUserEmail('');
    setUserId(null);
    setCanRespondToIncidents(false);
    setRuijieConnected(false);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <ScrollToTop />
      <PanicResponseProvider enabled={isAuthenticated && canRespondToIncidents} userId={userId}>
      {isAuthenticated && fireNotice && (
        <div className="fixed bottom-6 left-1/2 z-[110] max-w-md -translate-x-1/2 rounded-2xl border border-amber-400/40 bg-surface-raised px-5 py-3 text-sm text-amber-100 shadow-xl">
          {fireNotice}
          <button
            type="button"
            onClick={() => setFireNotice(null)}
            className="ml-3 text-amber-200 underline"
          >
            {t('common.close')}
          </button>
        </div>
      )}
      {isAuthenticated && fireOverlayIncident && (
        <FireOverlay
          incident={fireOverlayIncident}
          acceptLoading={fireAcceptLoading}
          onDismiss={() => {
            setFireDismissedIds((prev) => new Set(prev).add(fireOverlayIncident.id));
            setFireOverlayIncident(null);
          }}
          onAccept={async (id) => {
            setFireAcceptLoading(true);
            try {
              await acceptFireIncident(id);
              setFireOverlayIncident(null);
              window.dispatchEvent(new CustomEvent('fireIncidentUpdated', { detail: { id, status: 'accepted' } }));
            } catch (e) {
              const status = e?.response?.status;
              const data = e?.response?.data;
              if (status === 409) {
                setFireNotice(data?.acceptedByEmail || data?.message || t('fire.alreadyAccepted'));
                setFireDismissedIds((prev) => new Set(prev).add(id));
                setFireOverlayIncident(null);
                return;
              }
              console.error('[Fire] accept failed', e);
            } finally {
              setFireAcceptLoading(false);
            }
          }}
        />
      )}
      {/* Ключ сбрасывает диалог ассистента при смене пользователя и при выходе. */}
      <AssistantSessionProvider key={isAuthenticated ? userEmail : 'anonymous'}>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <AuthForm onLoginSuccess={handleLoginSuccess} />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <Dashboard
                setIsAuthenticated={setIsAuthenticated}
                role={userRole}
                userEmail={userEmail}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
  path="/speedtest-results"
  element={
    isAuthenticated ? (
      <SpeedtestResults
        setIsAuthenticated={setIsAuthenticated}
        userEmail={userEmail}
        userRole={userRole}
        onLogout={handleLogout}
      />
    ) : (
      <Navigate to="/" replace />
    )
  }
/>

        <Route
  path="/logs"
  element={
    isAuthenticated && userRole && (userRole.trim().toLowerCase() === 'engineer' || userRole.trim().toLowerCase() === 'admin') ? (
      <Logs
        setIsAuthenticated={setIsAuthenticated}
        userEmail={userEmail}
        userRole={userRole}
        onLogout={handleLogout}
      />
    ) : (
      <Navigate to="/" replace />
    )
  }
/>
        <Route
          path="/regions-stats"
          element={
            isAuthenticated ? (
              <RegionsStatsPage
        setIsAuthenticated={setIsAuthenticated}
        userEmail={userEmail}
        userRole={userRole}
        onLogout={handleLogout}
      />
    ) : (
      <Navigate to="/" replace />
    )
  }
/>
        <Route
          path="/alerts"
          element={
            isAuthenticated ? (
              <Alerts
                setIsAuthenticated={setIsAuthenticated}
                userEmail={userEmail}
                userRole={userRole}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/equipment"
          element={
            isAuthenticated ? (
              <Equipment
                userEmail={userEmail}
                userRole={userRole}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/panic-incidents"
          element={
            isAuthenticated ? (
              <PanicIncidents
                setIsAuthenticated={setIsAuthenticated}
                userEmail={userEmail}
                userRole={userRole}
                canRespondToIncidents={canRespondToIncidents}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/fire-incidents"
          element={
            isAuthenticated ? (
              <FireIncidents
                setIsAuthenticated={setIsAuthenticated}
                userEmail={userEmail}
                userRole={userRole}
                canRespondToIncidents={canRespondToIncidents}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/schools/:ip"
          element={
            isAuthenticated ? (
              <SchoolDashboard
                setIsAuthenticated={setIsAuthenticated}
                onLogout={handleLogout}
                userRole={userRole}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/schools/:ip/doors/:doorId"
          element={
            isAuthenticated ? (
              <HikvisionDeviceDashboard
                setIsAuthenticated={setIsAuthenticated}
                onLogout={handleLogout}
                userRole={userRole}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/schools/:ip/panic/:deviceId"
          element={
            isAuthenticated ? (
              <PanicStationDashboard
                setIsAuthenticated={setIsAuthenticated}
                onLogout={handleLogout}
                userRole={userRole}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/schools/:ip/nvr/:deviceId"
          element={
            isAuthenticated ? (
              <HikvisionNvrDashboard
                setIsAuthenticated={setIsAuthenticated}
                onLogout={handleLogout}
                userRole={userRole}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/assistant"
          element={
            isAuthenticated ? (
              <Assistant
                setIsAuthenticated={setIsAuthenticated}
                userEmail={userEmail}
                userRole={userRole}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/profile"
          element={
            isAuthenticated ? (
              <Profile
                setIsAuthenticated={setIsAuthenticated}
                userEmail={userEmail}
                userRole={userRole}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin"
          element={
            isAuthenticated && userRole && userRole.trim().toLowerCase() === 'admin' ? (
              <AdminPage
                setIsAuthenticated={setIsAuthenticated}
                userRole={userRole}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* Fallback route for unmatched paths */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
      </AssistantSessionProvider>
      </PanicResponseProvider>
    </Router>
  );
}

export default App;